import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    PLATFORM_ID,
    Renderer2,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterLinkWithHref } from '@angular/router';
import { Store } from '@ngrx/store';
import { DialogService } from '@pixelgenau/ngx-dialog';
import { SVGAwesomeDirective } from '@pixelgenau/ngx-svg-awesome/browser';
import { dealerActions } from 'libs/ng-common-lib/src/+state/dealer.actions';
import { dealerFeature } from 'libs/ng-common-lib/src/+state/dealer.reducer';
import { wholesaleActions } from 'libs/ng-common-lib/src/+state/wholesale.actions';
import { wholesaleFeature } from 'libs/ng-common-lib/src/+state/wholesale.reducer';
import { RouterLinkDirective } from 'libs/ng-common-lib/src/directives/router-link.directive';
import { IsNavDropdown } from 'libs/ng-common-lib/src/pipes/isNavDropdown.pipe';
import { MODEL_RANGE_FEATURE_TOKEN } from 'libs/ng-common-lib/src/provider/model-range.provider';
import { MatelsoService } from 'libs/ng-common-lib/src/services/matelso.service';
import { ViewportDetectionService } from 'libs/ng-common-lib/src/services/viewport-detection.service';
import { derivedAsync } from 'libs/ng-common-lib/src/utilities/derived-async';
import { injectM050C040MainnavigationPwStyle } from 'libs/ui/src/lib/modules/050-navigation-footer-overlays/m050-c040-mainnavigation-pw/styles';
import { of, Subject, switchMap, takeUntil } from 'rxjs';
import { LanguageSwitchDialogTag } from '../../../dialogs/language-switch/language-switch.dialog';
import { M000C010ImageComponent } from '../../000-primitives/m000-c010-image/m000-c010-image.component';

@Component({
    selector: 'wholesale-m050-c040-mainnavigation-pw',
    templateUrl: './m050-c040-mainnavigation-pw.component.html',
    styleUrls: ['./m050-c040-mainnavigation-pw.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        SVGAwesomeDirective,
        RouterLinkDirective,
        RouterLinkWithHref,
        RouterLinkActive,
        RouterLink,
        IsNavDropdown,
        M000C010ImageComponent,
    ],
})
export class M050C040MainnavigationPwComponent implements OnInit, OnDestroy {
    store = inject(Store);
    router = inject(Router);
    document = inject(DOCUMENT);
    viewport = inject(ViewportDetectionService);
    renderer = inject(Renderer2);
    platformId = inject(PLATFORM_ID);
    dialogService = inject(DialogService);
    elStyle = injectM050C040MainnavigationPwStyle();
    matelsoService = inject(MatelsoService, { optional: true });
    modelRangeFeature = inject(MODEL_RANGE_FEATURE_TOKEN, { optional: true });

    lang = this.store.selectSignal(wholesaleFeature.selectLanguage);
    nav = this.store.selectSignal(dealerFeature.selectMainNavigation);
    showMainNav = this.store.selectSignal(wholesaleFeature.selectShowMainNavigation);
    dealer = this.store.selectSignal(dealerFeature.selectActiveDealer);

    dropdownList = computed(() => this.nav()?.mainNav.map(() => ({ open: false })) ?? []);

    open = signal(false);
    isAtLeastOnDropdownOpen = signal(false);

    onDestroy$ = new Subject<void>();

    phoneNumber = derivedAsync(() => {
        const nav = this.nav();

        if (!nav) return undefined;
        if (!this.matelsoService) return of(nav.contact.phone.number);

        return this.matelsoService.getPhoneNumber$(nav.contact.phone.number);
    });

    isMobile$ = toObservable(this.viewport.isMobile);

    ngOnInit() {
        this.store.dispatch(dealerActions.setHasModelRangeFeature({ value: !!this.modelRangeFeature }));
        this.router.events.pipe(takeUntil(this.onDestroy$)).subscribe((event) => {
            if (event instanceof NavigationEnd) this.closeMobileNav();
        });
        if (isPlatformBrowser(this.platformId)) {
            this.isMobile$.pipe(takeUntil(this.onDestroy$)).subscribe((isMobile) => {
                if (!isMobile) {
                    this.closeMobileNav();
                    this.isAtLeastOnDropdownOpen.set(false);
                }
            });
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    async openLanguageDialog() {
        const nav = this.nav();

        if (!nav) return;

        this.dialogService
            .openDialog(LanguageSwitchDialogTag, nav.language.availableLanguages)
            .pipe(
                switchMap((ref) => ref.close$),
                takeUntil(this.onDestroy$),
            )
            .subscribe((newLang) => {
                if (newLang) {
                    this.store.dispatch(wholesaleActions.changeLanguage({ lang: newLang }));
                }
            });
    }

    openMobileNav() {
        this.open.set(true);
        this.renderer.addClass(this.document.body, 'overflow-hidden');
        this.renderer.addClass(this.document.body, 'lg:overflow-auto');
        this.renderer.addClass(this.document.body, 'fixed');
        this.renderer.addClass(this.document.body, 'lg:static');
    }

    closeMobileNav(): void {
        this.open.set(false);
        this.renderer.removeClass(this.document.body, 'overflow-hidden');
        this.renderer.removeClass(this.document.body, 'lg:overflow-auto');
        this.renderer.removeClass(this.document.body, 'fixed');
        this.renderer.removeClass(this.document.body, 'lg:static');
        for (let i = 0; i < this.dropdownList().length; i++) {
            const item = this.dropdownList()[i];
            item.open = false;
        }
        this.isAtLeastOnDropdownOpen.set(!!this.dropdownList().find((list) => list.open));
    }

    toggleDropdown(index: number) {
        if (!this.dropdownList()[index].open) {
            for (let i = 0; i < this.dropdownList.length; i++) {
                const item = this.dropdownList()[i];
                item.open = false;
            }
        }
        if (!this.open()) this.openMobileNav();

        this.dropdownList()[index].open = !this.dropdownList()[index].open;
        this.isAtLeastOnDropdownOpen.set(!!this.dropdownList().find((list) => list.open));
    }
}
